import { showErrorToast } from "../../../configurator/renderer/tools/helper";
import { useUiTranslator } from "../../../hooks/useUiTranslator";
import { usePostApiImportImportLineitem } from "../../../openapi/backendApi";
import { BaseModal } from "../BaseModal/BaseModal";
import { LabeledInput } from "../LabeledInput/LabeledInput";

import { JSX } from "react";
import { useState } from "react";

import { useGetByIdQuotesKey } from "@4pace-cpq/core/api";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

export const BtnImportConfigId = (): JSX.Element => {
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [quoteId, setQuoteId] = useState<string>("");
  const [isQuoteIdValid, setIsQuoteIdValid] = useState<boolean>(true);
  const [lineItemIds, setLineItemIds] = useState<string[]>([""]);
  const [isLineItemsValid, setIsLineItemsValid] = useState<boolean[]>([]);
  const [areAllLineItemsValid, setAreAllLineItemsValid] =
    useState<boolean>(true);
  const [isOverride, setIsOverride] = useState<boolean>(false);
  const t = useUiTranslator();
  const { mutate: mutateImportLineItem } = usePostApiImportImportLineitem();

  const handleOpenModal = (): void => {
    setIsModalOpen(true);
  };
  const handleCloseModal = (): void => {
    setIsModalOpen(false);
  };

  const checkIfGuid = (input: string): boolean => {
    const regexGUIDpattern =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return regexGUIDpattern.test(input);
  };

  const handleChangeQuoteId = (val: string): void => {
    const cleanedVal = val.trim();

    setIsQuoteIdValid(checkIfGuid(cleanedVal));

    setQuoteId(cleanedVal);
  };

  const handleChangeLineItemIds = (val: string, idx: number): void => {
    console.log("handleChangeLineItemIds");

    const valuesArr = val
      .split(",")
      .filter((value) => value.length)
      .map((value) => value.trim());

    const lineItemIdsBefore = lineItemIds.slice(0, idx > 0 ? idx : 0);
    const lineItemIdsAfter = lineItemIds.slice(
      idx < lineItemIds.length - 1 ? idx + 1 : lineItemIds.length - 1,
    );
    //0af11f7f-c313-4670-94b1-46bc27b665bc,0af11f7f-c313-4670-94b1-46bc27b665bc,0af11f7f-c313-4670-94b1-46bc27b665bc
    const newLineItemIds = [
      ...lineItemIdsBefore,
      ...valuesArr,
      ...lineItemIdsAfter,
    ];
    let allValuesAreValid: boolean = true;
    const newValidLineItems: boolean[] = newLineItemIds.map((value) => {
      if (value === "") {
        console.log("is empty string");

        return true;
      }
      const isValid = checkIfGuid(value);
      if (!isValid && allValuesAreValid) {
        console.log("is not valid");
        allValuesAreValid = false;
      }
      return isValid;
    });
    setAreAllLineItemsValid(allValuesAreValid);
    setIsLineItemsValid(newValidLineItems);

    if (newLineItemIds[newLineItemIds.length - 1].length) {
      newLineItemIds[newLineItemIds.length] = "";
    }
    setLineItemIds(newLineItemIds);
  };

  const handleSubmit = async (): Promise<void> => {
    mutateImportLineItem(
      {
        data: {
          lineItemIds: lineItemIds.filter((item) => !!item.length),
          projectId: quoteId || null,
          overrideExisting: isOverride,
        },
      },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          queryClient.invalidateQueries({
            queryKey: [useGetByIdQuotesKey],
          });
        },
        onError: (error) => {
          console.error("Error importing line item", error);
          const body = error.info as
            | {
                key?: string;
                message?: string;
                params?: Record<string, string>;
              }
            | undefined;
          if (body?.key) {
            showErrorToast(
              t(
                `CustomApp:${body.key}`,
                body.message ??
                  "An error occurred while processing your request.",
                { params: body.params },
              ),
            );
          } else {
            showErrorToast(
              body?.message ??
                "An error occurred while processing your request.",
            );
          }
        },
      },
    );
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        onClick={handleOpenModal}
        startIcon={<FileUploadIcon />}
      >
        <Typography>
          {t("CustomApp:btn.importConfigId", "Import configuration")}
        </Typography>
      </Button>
      <BaseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={t("CustomApp:btn.importConfigId", "Import configuration")}
      >
        <Box display="flex">
          <Box p={2}>
            <LabeledInput
              fullWidth
              sx={{ minWidth: "22em" }}
              error={!isQuoteIdValid && quoteId.length > 0}
              label={t("CustomApp:label.projectId", "Project ID")}
              value={quoteId}
              handleChange={(val) => handleChangeQuoteId(val)}
              helperText={
                !isQuoteIdValid && quoteId.length > 0
                  ? t("CustomApp:error.invalidGUID", "Invalid GPS ID")
                  : ""
              }
              disabled={!!lineItemIds[0].length}
            />
          </Box>
          <Box p={2} bgcolor="rgba(10,0,0,.05)">
            <Typography
              variant="body1"
              sx={{
                margin: "0",
                fontWeight: "500",
                fontSize: "0.875rem",
                lineHeight: "1.57",
                letterSpacing: " 0.00714em",
                color: "rgba(0, 0, 0, 0.6)",
              }}
            >
              {t("CustomApp:label.gearProSelectIds", "GPS IDs")}
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {lineItemIds.map((LIID, index) => {
                return (
                  <LabeledInput
                    fullWidth
                    sx={{ minWidth: "22em" }}
                    key={"lineItemIdInput" + index}
                    disabled={!!quoteId.length}
                    value={LIID}
                    error={!isLineItemsValid[index] && LIID.length > 0}
                    handleChange={(val) => handleChangeLineItemIds(val, index)}
                    helperText={
                      !isLineItemsValid[index] && LIID.length > 0
                        ? t("CustomApp:error.invalidGUID", "Invalid GPS ID")
                        : ""
                    }
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
        <Box display="flex" justifyContent="flex-end" pt={2} gap={2}>
          <FormControlLabel
            label={t("CustomApp:chkbx.overrideCart", "Override")}
            control={
              <Checkbox
                checked={isOverride}
                onChange={() => {
                  setIsOverride(!isOverride);
                }}
              />
            }
          />
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !(quoteId.length && isQuoteIdValid) &&
              !(lineItemIds[0].length && areAllLineItemsValid)
            }
          >
            {t("CustomApp:btn.import", "Import")}
          </Button>
        </Box>
      </BaseModal>
    </>
  );
};
